import type { ElementTransformer } from "@lexical/markdown";
import {
	$convertFromMarkdownString,
	$convertToMarkdownString,
	TRANSFORMERS,
} from "@lexical/markdown";
import {
	$createHorizontalRuleNode,
	$isHorizontalRuleNode,
	HorizontalRuleNode,
} from "@lexical/react/LexicalHorizontalRuleNode";
import {
	$createTableCellNode,
	$createTableNode,
	$createTableRowNode,
	$isTableCellNode,
	$isTableNode,
	$isTableRowNode,
	TableCellHeaderStates,
	TableCellNode,
	TableNode,
	TableRowNode,
} from "@lexical/table";
import type { LexicalNode } from "lexical";
import { $isParagraphNode, $isTextNode } from "lexical";

// GFM 테이블·HR 변환기 — @lexical/markdown에 내장돼 있지 않아 Lexical
// 플레이그라운드(MIT) MarkdownTransformers 구현을 포팅. 어드민(tenbagger-web-admin)의
// markdown-transformers.ts와 동일 세트 — CMS가 저장한 md를 같은 방언으로 파싱한다.

export const HR: ElementTransformer = {
	dependencies: [HorizontalRuleNode],
	export: (node: LexicalNode) => {
		return $isHorizontalRuleNode(node) ? "***" : null;
	},
	regExp: /^(---|\*\*\*|___)\s?$/,
	replace: (parentNode, _1, _2, isImport) => {
		const line = $createHorizontalRuleNode();
		if (isImport || parentNode.getNextSibling() != null) {
			parentNode.replace(line);
		} else {
			parentNode.insertBefore(line);
		}
		line.selectNext();
	},
	type: "element",
};

const TABLE_ROW_REG_EXP = /^\|(.+)\|\s?$/;
const TABLE_ROW_DIVIDER_REG_EXP = /^(\| ?:?-*:? ?)+\|\s?$/;

function getTableColumnsSize(table: TableNode) {
	const row = table.getFirstChild();
	return $isTableRowNode(row) ? row.getChildrenSize() : 0;
}

function createTableCell(textContent: string): TableCellNode {
	const cleaned = textContent.replace(/\\n/g, "\n");
	const cell = $createTableCellNode(TableCellHeaderStates.NO_STATUS);
	$convertFromMarkdownString(cleaned, MARKDOWN_TRANSFORMERS, cell);
	return cell;
}

function mapToTableCells(textContent: string): Array<TableCellNode> | null {
	const match = textContent.match(TABLE_ROW_REG_EXP);
	if (!match || !match[1]) {
		return null;
	}
	return match[1].split("|").map((text) => createTableCell(text.trim()));
}

export const TABLE: ElementTransformer = {
	dependencies: [TableNode, TableRowNode, TableCellNode],
	export: (node: LexicalNode) => {
		if (!$isTableNode(node)) {
			return null;
		}

		const output: string[] = [];

		for (const row of node.getChildren()) {
			const rowOutput: string[] = [];
			if (!$isTableRowNode(row)) {
				continue;
			}

			let isHeaderRow = false;
			for (const cell of row.getChildren()) {
				// 셀 내부도 마크다운으로 직렬화 (개행은 이스케이프)
				if ($isTableCellNode(cell)) {
					rowOutput.push(
						$convertToMarkdownString(MARKDOWN_TRANSFORMERS, cell)
							.replace(/\n/g, "\\n")
							.trim(),
					);
					if (cell.__headerState === TableCellHeaderStates.ROW) {
						isHeaderRow = true;
					}
				}
			}

			output.push(`| ${rowOutput.join(" | ")} |`);
			if (isHeaderRow) {
				output.push(`| ${rowOutput.map(() => "---").join(" | ")} |`);
			}
		}

		return output.join("\n");
	},
	regExp: TABLE_ROW_REG_EXP,
	replace: (parentNode, _1, match) => {
		// 구분선 행(| --- | --- |)이면 직전 행을 헤더로 승격
		if (TABLE_ROW_DIVIDER_REG_EXP.test(match[0])) {
			const table = parentNode.getPreviousSibling();
			if (!table || !$isTableNode(table)) {
				return;
			}

			const rows = table.getChildren();
			const lastRow = rows[rows.length - 1];
			if (!lastRow || !$isTableRowNode(lastRow)) {
				return;
			}

			lastRow.getChildren().forEach((cell) => {
				if (!$isTableCellNode(cell)) {
					return;
				}
				cell.setHeaderStyles(
					TableCellHeaderStates.ROW,
					TableCellHeaderStates.ROW,
				);
			});

			parentNode.remove();
			return;
		}

		const matchCells = mapToTableCells(match[0]);
		if (matchCells == null) {
			return;
		}

		const rows = [matchCells];
		let sibling = parentNode.getPreviousSibling();
		let maxCells = matchCells.length;

		// 연속된 테이블 행 문단들을 위로 수집해 하나의 테이블로 병합
		while (sibling) {
			if (!$isParagraphNode(sibling)) {
				break;
			}
			if (sibling.getChildrenSize() !== 1) {
				break;
			}
			const firstChild = sibling.getFirstChild();
			if (!$isTextNode(firstChild)) {
				break;
			}
			const cells = mapToTableCells(firstChild.getTextContent());
			if (cells == null) {
				break;
			}

			maxCells = Math.max(maxCells, cells.length);
			rows.unshift(cells);
			const previousSibling = sibling.getPreviousSibling();
			sibling.remove();
			sibling = previousSibling;
		}

		const table = $createTableNode();
		for (const cells of rows) {
			const tableRow = $createTableRowNode();
			table.append(tableRow);
			for (let i = 0; i < maxCells; i++) {
				tableRow.append(i < cells.length ? cells[i] : createTableCell(""));
			}
		}

		const previousSibling = parentNode.getPreviousSibling();
		if (
			$isTableNode(previousSibling) &&
			getTableColumnsSize(previousSibling) === maxCells
		) {
			previousSibling.append(...table.getChildren());
			parentNode.remove();
		} else {
			parentNode.replace(table);
		}

		table.selectEnd();
	},
	type: "element",
};

/** learn 렌더러의 md 변환기 세트 — CMS(어드민 에디터)와 동일한 방언 */
export const MARKDOWN_TRANSFORMERS = [TABLE, HR, ...TRANSFORMERS];
